import { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ArrowLeft, Check, Mail } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Field, Input, cx } from "../../components/ui"
import { AuthLayout } from "./AuthLayout"

const LENGTH = 6

/**
 * Forgot password, in two steps on one page.
 *
 * Step one always reports success, whether or not the address has an account.
 * A different answer for a known address would turn this into a free way of
 * finding out who has one.
 */
export function ForgotPasswordPage() {
	const { config, adoptSession, toast } = useApp()
	const navigate = useNavigate()
	const [params] = useSearchParams()

	const [email, setEmail] = useState(params.get("email") || "")
	const [challenge, setChallenge] = useState(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)

	async function request(event) {
		event.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const started = await api.forgotPassword(email.trim())
			// `challenge_id` is null for an address with no account. The code step
			// still shows, so the page answers the same either way.
			setChallenge(started || { challenge_id: null })
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	if (challenge) {
		return (
			<ChooseNewPassword
				challenge={challenge}
				email={email.trim()}
				minimum={config?.password_min_length ?? 10}
				onBack={() => setChallenge(null)}
				onDone={async (session) => {
					await adoptSession(session.token, session.user)
					toast("Password updated. You are signed in.", "success")
					navigate("/app", { replace: true })
				}}
			/>
		)
	}

	return (
		<AuthLayout
			title="Reset your password"
			description="Tell us the address you sign in with and we will email a six-digit code."
			footer={
				<Link to="/sign-in" className="font-medium text-brand-700 hover:underline">
					Back to sign in
				</Link>
			}
		>
			<form onSubmit={request} className="space-y-3.5">
				<Field label="Work email" error={error}>
					<Input
						type="email"
						autoComplete="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						placeholder="you@company.com"
						required
						autoFocus
					/>
				</Field>
				<Button
					type="submit"
					variant="primary"
					size="lg"
					className="w-full"
					icon={Mail}
					busy={busy}
					disabled={busy || !email.trim()}
				>
					Email me a code
				</Button>
			</form>
		</AuthLayout>
	)
}

function ChooseNewPassword({ challenge, email, minimum, onBack, onDone }) {
	const [digits, setDigits] = useState(Array(LENGTH).fill(""))
	const [password, setPassword] = useState("")
	const [confirm, setConfirm] = useState("")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)
	const inputs = useRef([])

	useEffect(() => {
		inputs.current[0]?.focus()
	}, [])

	const code = digits.join("")

	function setDigit(index, value) {
		const cleaned = value.replace(/\D/g, "")
		if (!cleaned) {
			setDigits((current) => current.map((d, i) => (i === index ? "" : d)))
			return
		}
		const next = [...digits]
		cleaned.split("").forEach((char, offset) => {
			if (index + offset < LENGTH) next[index + offset] = char
		})
		setDigits(next)
		inputs.current[Math.min(index + cleaned.length, LENGTH - 1)]?.focus()
	}

	async function submit(event) {
		event.preventDefault()
		if (password !== confirm) {
			setError("The two passwords do not match.")
			return
		}
		setBusy(true)
		setError(null)
		try {
			const session = await api.resetPassword({
				challenge_id: challenge.challenge_id,
				code,
				new_password: password
			})
			await onDone(session)
		} catch (cause) {
			// An unknown address gets no challenge at all, so its code step can
			// only end here - worded the same as a mistyped or expired code, so
			// the two cannot be told apart.
			setError(
				cause.code === "code_invalid" || cause.code === "code_expired"
					? "That code is not right or has expired. Check the email, or go back and ask for a new one."
					: cause.message
			)
		} finally {
			setBusy(false)
		}
	}

	return (
		<AuthLayout
			title="Choose a new password"
			description={`We emailed a ${LENGTH}-digit code to ${email}.`}
		>
			<form onSubmit={submit} className="space-y-4">
				<div>
					<span className="mb-1.5 block text-[0.8125rem] font-medium text-ink-700">
						Code from the email
					</span>
					<div
						className="flex justify-between gap-2"
						onPaste={(event) => {
							const text = event.clipboardData?.getData("text") || ""
							if (/\d/.test(text)) {
								event.preventDefault()
								setDigit(0, text)
							}
						}}
					>
						{digits.map((digit, index) => (
							<input
								key={index}
								ref={(node) => {
									inputs.current[index] = node
								}}
								value={digit}
								onChange={(event) => setDigit(index, event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Backspace" && !digits[index] && index > 0) {
										inputs.current[index - 1]?.focus()
									}
								}}
								inputMode="numeric"
								autoComplete={index === 0 ? "one-time-code" : "off"}
								maxLength={LENGTH}
								aria-label={`Digit ${index + 1}`}
								disabled={busy}
								className={cx(
									"h-12 w-full rounded-lg border text-center text-lg font-semibold tabular",
									"text-ink-900 transition-colors focus:outline-none",
									"border-ink-200 bg-white focus:border-brand-400"
								)}
							/>
						))}
					</div>
				</div>

				{challenge.debug_code && (
					<p className="rounded-lg bg-amber-50 px-3 py-2 text-2xs leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200">
						<strong>Local development.</strong> The code is{" "}
						<span className="font-mono font-semibold">{challenge.debug_code}</span>.
					</p>
				)}

				<Field
					label="New password"
					hint={`At least ${minimum} characters, with a mix of letters and numbers.`}
				>
					<Input
						type="password"
						autoComplete="new-password"
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						required
					/>
				</Field>

				<Field label="Confirm new password" error={error}>
					<Input
						type="password"
						autoComplete="new-password"
						value={confirm}
						onChange={(event) => setConfirm(event.target.value)}
						required
					/>
				</Field>

				<Button
					type="submit"
					variant="primary"
					size="lg"
					className="w-full"
					icon={Check}
					busy={busy}
					disabled={busy || code.length !== LENGTH || !password}
				>
					Set password and sign in
				</Button>

				<Button type="button" size="sm" variant="ghost" icon={ArrowLeft} onClick={onBack}>
					Use a different email
				</Button>
			</form>
		</AuthLayout>
	)
}
